import { Injectable, inject, signal } from '@angular/core';
import type { RecordingEnvelope } from '../tracking-demo.models';
import { HttpClient, HttpContext, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { SHOW_LOADER } from '../../../../core/interceptors/micro-service-logging.interceptor';
import { AuthService } from '../../../../core/services/auth.service';
import { TrackingAdminTokenService } from './tracking-admin-token.service';
import { resolveApiBase } from '../../../public/credee-demo-livekit/livekit-demo.config';
import type {
  TrackingSession, TrackingParticipant, DashboardMetrics, BlacklistedIp,
  AuditLogEntry, Paged, HistoryQuery, DisconnectReason, SessionReport,
  SessionCostSummary, TrackingDataType,
  RagAudit, CallAudit, FailedQueryRow, InsightsAggregate,
} from '../tracking-demo.models';
import type { PrejoinDumpWithVisitor, PrejoinLiveList } from '../prejoin-live.models';

/**
 * REST client for the Tracking Demo admin surface
 * (`${liveCall.baseUrl}live-call/api/tracking/*`).
 *
 * Every call suppresses the global loader (SHOW_LOADER=false — this is a
 * background dashboard) and attaches the X-Admin-Token header plus the logged-in
 * admin's X-Admin-Name/X-Admin-Id (attribution only — audit rows name the actual
 * admin instead of "Prism Admin"). A 401 invalidates the stored token so the page
 * re-prompts. Errors bubble to the caller.
 *
 * COMPONENT-provided (like the store and socket), not root: `productKey` scopes every
 * tracking call to the product panel the dashboard was opened under (route data →
 * TrackingDemoComponent sets it before store.start()), and that per-panel state must
 * die with the page.
 */
@Injectable()
export class TrackingApiService {
  private readonly http = inject(HttpClient);
  private readonly tokens = inject(TrackingAdminTokenService);
  private readonly auth = inject(AuthService);
  private readonly cfg = environment.liveCall;

  private getBaseUrl(): string {
    let base = this.cfg.baseUrl;
    try {
      const apiBase = resolveApiBase();
      const suffix = '/livecall';
      if (apiBase.startsWith('http://') || apiBase.startsWith('https://')) {
        base = apiBase.endsWith(suffix)
          ? `${apiBase.slice(0, -suffix.length)}/`
          : `${apiBase}/`;
      }
    } catch {
      // Fallback to cfg.baseUrl
    }
    return base.endsWith('/') ? base : `${base}/`;
  }

  readonly productKey = signal<string | null>(null);

  private url(endpoint: string): string {
    return `${this.getBaseUrl()}live-call/api/tracking/${endpoint}`;
  }

  /**
   * Append `product=` to a tracking query so the panel shows ONLY the product it was
   * opened under. `productKey` is set from the route; null (or the combined view)
   * leaves it off, and the backend then answers unscoped exactly as before.
   *
   * Without this the dashboard read every product's rows while its heading claimed
   * one product's name — a Practina panel listed Credee demos.
   */
  private scoped(endpoint: string): string {
    const key = (this.productKey() || '').trim();
    if (!key || key === 'all') return endpoint;
    return endpoint + (endpoint.includes('?') ? '&' : '?')
      + `product=${encodeURIComponent(key)}`;
  }

  /** The products this dashboard can filter by (packs on the backend, not a hardcoded list). */
  getProducts(): Promise<{ items: Array<{ keyname: string; name: string; productId: number | null; registered: boolean }>; default: string }> {
    return this.get('products');
  }

  /**
   * Absolute URL of a served demo asset (deck page / financing flow HTML) — the SAME allowlisted
   * static route the prospect's screen loads (`${baseUrl}live-call/api/assets/<file>`). The covert
   * Live View renders it in an iframe to mirror the exact page. No admin token needed (public asset).
   *
   * `productToken` scopes the lookup to the owning product's deck, exactly like the demo
   * itself does — the backend serves the DEFAULT product's files (and caches under its
   * namespace) when the token is missing, so a non-default product's page would miss.
   */
  assetUrl(filename: string, productToken?: string | null): string {
    const url = `${this.getBaseUrl()}live-call/api/assets/${encodeURIComponent(filename)}`;
    return productToken ? `${url}?productToken=${encodeURIComponent(productToken)}` : url;
  }

  /**
   * Absolute URL of one live deck slide's full HTML document — the SAME endpoint the
   * prospect's screen iframed during the demo (`{root}livecall/products/{slug}/slides/{idx}`,
   * served by the revamp `livecall_agent`, a sibling mount of the tracking API under the
   * same origin). `slug` is the slide's `deckKey`; `index` is its 0-based `slideIndex`.
   * Public (no admin token). Lets the report re-render the exact page that was shown.
   */
  deckSlideUrl(slug: string, index: number): string {
    return `${this.getBaseUrl()}livecall/products/${encodeURIComponent(slug)}/slides/${index}`;
  }

  wsUrl(): string {
    const base = this.getBaseUrl();
    let abs: string;
    if (/^https?:\/\//i.test(base)) {
      abs = base;
    } else {
      // relative (dev proxy, e.g. '/ai/') → resolve against the page origin
      abs = `${window.location.origin}${base.startsWith('/') ? '' : '/'}${base}`;
    }
    const wsBase = abs.replace(/^http/i, 'ws');
    return `${wsBase}lead_gen/ws/pipeline/live-call/tracking`;
  }

  /** Admin "Watch live screen" socket (backend ws/live_screen.py), same tunnel. */
  screenWsUrl(): string {
    return `${this.wsUrl()}-screen`;
  }

  private ctx(): HttpContext {
    return new HttpContext().set(SHOW_LOADER, false);
  }

  /** The logged-in CRM admin's display name (audit attribution; null when unknown).
   *  The backend's user record usually has no `name` field — only firstName/lastName —
   *  so compose one, and fall back to the sessionStorage `currentUser` copy when the
   *  in-memory user isn't populated yet (e.g. a hard reload mid-auth). */
  adminName(): string | null {
    const fromUser = this.displayName(this.auth.getUser());
    if (fromUser) return fromUser;
    try {
      return this.displayName(JSON.parse(sessionStorage.getItem('currentUser') ?? 'null'));
    } catch {
      return null;
    }
  }

  private displayName(
    u: { name?: string; firstName?: string; lastName?: string } | null,
  ): string | null {
    if (!u) return null;
    const composed = [u.firstName, u.lastName].filter(Boolean).join(' ');
    return (u.name || composed).trim() || null;
  }

  /** The logged-in CRM admin's id (audit attribution; null when unknown). */
  adminId(): string | null {
    return this.auth.getUserId();
  }

  private headers(): HttpHeaders {
    // X-Admin-Name/X-Admin-Id are ATTRIBUTION ONLY (audit rows name the actual admin
    // instead of "Prism Admin"); auth stays the token/CRM gate. URI-encoded because
    // HTTP header values must be latin-1; the backend unquotes.
    let h = new HttpHeaders({ 'X-Admin-Token': this.tokens.token() || '' });
    const name = this.adminName();
    if (name) h = h.set('X-Admin-Name', encodeURIComponent(name));
    const id = this.adminId();
    if (id) h = h.set('X-Admin-Id', encodeURIComponent(id));
    return h;
  }

  private async get<T>(endpoint: string): Promise<T> {
    try {
      return await firstValueFrom(
        this.http.get<T>(this.url(endpoint), { context: this.ctx(), headers: this.headers() }),
      );
    } catch (err) {
      this.handle(err);
      throw err;
    }
  }

  private async post<T>(endpoint: string, body: any): Promise<T> {
    try {
      return await firstValueFrom(
        this.http.post<T>(this.url(endpoint), body, { context: this.ctx(), headers: this.headers() }),
      );
    } catch (err) {
      this.handle(err);
      throw err;
    }
  }

  private async del<T>(endpoint: string): Promise<T> {
    try {
      return await firstValueFrom(
        this.http.delete<T>(this.url(endpoint), { context: this.ctx(), headers: this.headers() }),
      );
    } catch (err) {
      this.handle(err);
      throw err;
    }
  }

  private handle(err: unknown): void {
    if (err instanceof HttpErrorResponse && err.status === 401) {
      this.tokens.invalidate();
    }
  }

  // ── reads ──
  getLiveSessions(page = 1, pageSize = 100, dataType?: TrackingDataType): Promise<Paged<TrackingSession>> {
    const dt = dataType ? `&dataType=${dataType}` : '';
    return this.get(this.scoped(`live-sessions?page=${page}&pageSize=${pageSize}${dt}`));
  }
  getSession(sessionId: string): Promise<TrackingSession> {
    return this.get(`sessions/${encodeURIComponent(sessionId)}`);
  }
  getParticipants(sessionId: string): Promise<{ participants: TrackingParticipant[] }> {
    return this.get(`sessions/${encodeURIComponent(sessionId)}/participants`);
  }
  /** The full session report (History detail): everything that happened in one demo. */
  getReport(sessionId: string): Promise<SessionReport> {
    return this.get(`sessions/${encodeURIComponent(sessionId)}/report`);
  }
  /** Build (or fetch) the recording's per-leg loudness envelope for the debug waveforms. */
  buildEnvelope(sessionId: string): Promise<{ envelope: RecordingEnvelope }> {
    return this.post(`sessions/${encodeURIComponent(sessionId)}/envelope`, {});
  }
  /**
   * Attribute one RAG lookup's answer back to the chunks behind it (admin, on demand —
   * it spends a model call). The chunks and answer being displayed are sent along, so the
   * verdict is of exactly what is on screen and the retrieval chain stays implemented once.
   * The result is stored server-side against the lookup, so re-opening the report reads it
   * back rather than paying again.
   */
  auditRagLookup(sessionId: string, body: {
    callId: number; lookupIndex: number; question: string; answer: string;
    chunks: Array<{ n: number; title: string; subtitle: string; text: string }>;
  }): Promise<RagAudit> {
    return this.post(`sessions/${encodeURIComponent(sessionId)}/rag-audit`, body);
  }
  /** Force a (re-)audit of this session's call. Runs the full backend audit and
   *  returns the fresh CallAudit (stored server-side; the next report read has it). */
  runCallAudit(sessionId: string, callId?: number | null): Promise<CallAudit> {
    return this.post(`sessions/${encodeURIComponent(sessionId)}/audit`,
                     callId ? { callId } : {});
  }
  /** The cross-call failed-query list (scanned from audit_data, bounded by days). */
  getFailedQueries(opts: { days?: number; reason?: string; severity?: string;
                           page?: number; pageSize?: number } = {}):
      Promise<{ items: FailedQueryRow[]; total: number; page: number; pageSize: number }> {
    const p = new URLSearchParams({ days: String(opts.days ?? 30),
                                    page: String(opts.page ?? 1),
                                    pageSize: String(opts.pageSize ?? 50) });
    if (opts.reason) p.set('reason', opts.reason);
    if (opts.severity) p.set('severity', opts.severity);
    return this.get(this.scoped(`audit/failed-queries?${p.toString()}`));
  }
  /** The same list as JSONL training data (raw text — the caller downloads it). */
  async exportFailedQueries(days = 30): Promise<string> {
    return await firstValueFrom(
      this.http.get(this.url(this.scoped(`audit/failed-queries/export?days=${days}`)),
                    { context: this.ctx(), headers: this.headers(),
                      responseType: 'text' }),
    );
  }
  /**
   * The public deck manifest (`live-call/api/decks`), flattened to `{key, file, title}`.
   * Lets the Live View resolve a deck slide's `deckKey` → asset file and mirror the exact page
   * even when the slide event didn't carry `assetFile` (older sessions). Best-effort → [].
   *
   * `productToken` (a product keyname) picks WHICH product's manifest — the backend
   * answers with the DEFAULT product's catalog when it's absent, so every other
   * product's page keys missed the map and slides fell through to the product-slides
   * URL, which 404s on a page key. Pass the session's own product; null keeps the
   * default (correct for pre-rollout rows).
   *
   * Reads BOTH manifest shapes: the backend serves nested categories once
   * DECK_GROUPS_ENABLED is on, and flat pages until then. Only `key` → `file` matters here,
   * so the categories are flattened away. A page key repeats across categories in group
   * mode; the first wins, which is the same slide the flat manifest would have named.
   */
  async getDecks(productToken?: string | null): Promise<Array<{ key: string; file: string; title?: string }>> {
    // getBaseUrl(), not cfg.baseUrl — under an `?api=` override the manifest MUST come from the
    // same backend as the session data, or every key misses and the slides fall through to the
    // product-slides URL, which 404s on a page key.
    const base = this.getBaseUrl();
    const qs = productToken ? `?productToken=${encodeURIComponent(productToken)}` : '';
    type Page = { key: string; file: string; title?: string };
    try {
      const r = await firstValueFrom(
        this.http.get<{ decks?: Page[]; groups?: Array<{ pages: Page[] }> }>(
          `${base}live-call/api/decks${qs}`, { context: this.ctx() }),
      );
      if (r?.groups?.length) return r.groups.flatMap((g) => g.pages ?? []);
      return r?.decks || [];
    } catch {
      return [];
    }
  }
  /** Token usage + USD spend for one demo (live_call_api_costs breakdown; works mid-call). */
  getSessionCosts(sessionId: string): Promise<SessionCostSummary> {
    return this.get(`sessions/${encodeURIComponent(sessionId)}/costs`);
  }
  getMetrics(dataType?: TrackingDataType): Promise<DashboardMetrics> {
    return this.get(this.scoped(dataType ? `metrics?dataType=${dataType}` : 'metrics'));
  }
  /** Cross-session behavioural aggregate (rage/dead/scroll + click heatmap). */
  getInsights(days = 30): Promise<InsightsAggregate> {
    return this.get(this.scoped(`insights?days=${days}`));
  }
  getHistory(q: HistoryQuery): Promise<Paged<TrackingSession>> {
    const params = new URLSearchParams();
    Object.entries(q).forEach(([k, v]) => {
      if (v !== undefined && v !== null && `${v}`.trim() !== '') params.set(k, `${v}`);
    });
    return this.get(this.scoped(`history?${params.toString()}`));
  }
  getBlacklist(includeInactive = false): Promise<{ items: BlacklistedIp[]; total: number }> {
    return this.get(`blacklist?includeInactive=${includeInactive ? 1 : 0}`);
  }
  getAuditLogs(page = 1, pageSize = 50): Promise<Paged<AuditLogEntry>> {
    return this.get(`audit-logs?page=${page}&pageSize=${pageSize}`);
  }
  /** Never-joined visitors' rrweb captures, newest first, scoped to this panel's product. */
  getPrejoinDumps(page = 1, pageSize = 25): Promise<Paged<PrejoinDumpWithVisitor>> {
    return this.get(this.scoped(`prejoin-dumps?page=${page}&pageSize=${pageSize}`));
  }
  /** Visitors on the setup form right now, scoped to this panel's product. */
  getPrejoinLive(): Promise<PrejoinLiveList> {
    return this.get(this.scoped('prejoin-live'));
  }

  // ── mutations ──
  // Every product's live room registry lives on the ONE unified backend mount
  // (live-call), so room mutations and blacklist mutations ride the same URL builder.
  disconnect(sessionId: string, reason: DisconnectReason, note?: string): Promise<any> {
    return this.post(`sessions/${encodeURIComponent(sessionId)}/disconnect`, { reason, note });
  }
  /**
   * Mint the sid-scoped observer token that lets an admin JOIN the demo as a hidden,
   * receive-only participant. Returns the token + the ROOM sid the observer socket must
   * subscribe with (a demo's primary sid ⇄ room sid differ). Also hands back the admin
   * token so the observer tab can call the control REST when the surface requires it.
   */
  mintObserverToken(sessionId: string):
    Promise<{ ok: boolean; observerToken: string; roomSessionId: string }> {
    return this.post(`sessions/${encodeURIComponent(sessionId)}/observer-token`, {});
  }
  /** The current admin token (handed to the observer tab for the control REST). */
  adminToken(): string | null {
    return this.tokens.token() || null;
  }
  /** Force-mute / unmute one participant ('host' or a guest participantId). */
  muteParticipant(sessionId: string, participantId: string, muted: boolean, reason?: string): Promise<any> {
    return this.post(
      `sessions/${encodeURIComponent(sessionId)}/participants/${encodeURIComponent(participantId)}/mute`,
      { muted, reason },
    );
  }
  /** Remove a guest from the call (terminal block — they cannot rejoin). */
  removeParticipant(sessionId: string, participantId: string, reason?: string): Promise<any> {
    return this.post(
      `sessions/${encodeURIComponent(sessionId)}/participants/${encodeURIComponent(participantId)}/remove`,
      { reason },
    );
  }
  blacklistIp(ip: string, reason: string, sessionId?: string): Promise<any> {
    return this.post('blacklist', { ip, reason, sessionId });
  }
  removeBlacklistIp(ip: string): Promise<any> {
    return this.del(`blacklist/${encodeURIComponent(ip)}`);
  }
}
